import { Component, ElementRef, OnDestroy, signal, viewChild } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';

interface IFeatures {
  img: string;
  label: string;
  description: string;
}

interface INavLink {
  fragment: string;
  label: string;
}
@Component({
  selector: 'app-landing-page',
  imports: [RouterLink],
  templateUrl: './landing-page.html',
  styleUrl: './landing-page.css',
})
export class LandingPage implements OnDestroy {
  constructor(
    private router: Router,
    private viewportScroller: ViewportScroller,
  ) {
    this.viewportScroller.setOffset(() => [0, this.headerOffset()]);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() =>
        this.activeFragment.set(this.router.parseUrl(this.router.url).fragment ?? ''),
      );
  }

  private readonly navBar = viewChild<ElementRef<HTMLElement>>('navBar');
  private readonly mobileNav = viewChild<ElementRef<HTMLElement>>('mobileNav');

  protected showNav = signal<boolean>(false);
  protected currentYear: number = new Date().getFullYear();
  protected activeFragment = signal<string>('');

  protected navLinks: INavLink[] = [
    { fragment: 'overview', label: 'Overview' },
    { fragment: 'features', label: 'Features' },
    { fragment: 'how-it-works', label: 'How it works' },
  ];

  protected features: IFeatures[] = [
    {
      img: 'book.svg',
      label: 'Academic setup',
      description:
        'Create academic years, terms, levels and subjects from one structured workspace.',
    },
    {
      img: 'graduate.svg',
      label: 'Class management',
      description:
        'Organise classes, assign teachers and manage students with clear, searchable records.',
    },
    {
      img: 'user.svg',
      label: 'User management',
      description:
        'Onboard administrators and teachers with the right roles and access from day one.',
    },
    {
      img: 'card.svg',
      label: 'Student fees',
      description:
        'Track fee structures, payments and outstanding balances with a reliable transaction history.',
    },
    {
      img: 'chart.svg',
      label: 'Assessments',
      description:
        'Configure score structures, enter results and automatically scale class scores to weighting.',
    },
    {
      img: 'document.svg',
      label: 'Audit logs',
      description:
        'See who changed what and when, giving school leaders greater control and accountability.',
    },
  ];

  protected goToFragment(fragment: string): void {
    this.activeFragment.set(fragment);
    this.router.navigate([], { fragment, queryParamsHandling: 'preserve' });

    this.viewportScroller.scrollToAnchor(fragment);
    this.showNav.set(false);
  }

  ngOnDestroy(): void {
    this.viewportScroller.setOffset([0, 0]);
  }

  private headerOffset(): number {
    const mobileNav = this.mobileNav()?.nativeElement;

    if (mobileNav && mobileNav.getClientRects().length > 0) {
      return mobileNav.offsetHeight;
    }

    return this.navBar()?.nativeElement.offsetHeight ?? 0;
  }
}
